import React, { useState, useEffect, useContext } from 'react';
import { Card, CardContent } from './ui/card';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Label } from './ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from './ui/select';
import { useTranslation } from '../hooks/useTranslation';
import { 
  BarChart2, Search, ChevronDown, Calendar, Lock, ArrowRight, ArrowLeft, Loader2
} from 'lucide-react';
import { useAuth } from '../AuthContext'; // Import auth context
import apiClient from '../apiClient'; // Import API client

// Utility function for class names
function cn(...classes) {
  return classes.filter(Boolean).join(' ');
}

// UnitDetailsForm Component
export default function UnitDetailsForm({
  onSubmit,
  onBackToDashboard,
  className,
  formData,
  setFormData
}) {
  const { t } = useTranslation();
  const [products, setProducts] = useState([]);
  const [productionLines, setProductionLines] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState(null);

  // Use auth context
  const { user, getAuthHeaders } = useAuth();

  // Fetch products and production lines on mount
  useEffect(() => {
    if (user) {
      fetchUnitDetailsOptions();
    } else {
      setError('Please login to continue');
      setIsLoading(false);
    }
  }, [user]);

  const fetchUnitDetailsOptions = async () => {
    try {
      setIsLoading(true);
      setError(null);

      console.log('🔄 Fetching products and production lines from API...');
      console.log('🔑 User:', user);
      console.log('🌐 API Base URL:', apiClient);

      // Use apiClient to fetch data
      const [productsResponse, productionLinesResponse] = await Promise.all([
        apiClient.get('/api/frontend/products/get/'),
        apiClient.get('/api/frontend/production-lines/get/')
      ]);

      console.log('📡 Full API responses:', {
        products: { 
          status: productsResponse.status, 
          ok: productsResponse.ok,
          statusText: productsResponse.statusText,
          headers: productsResponse.headers
        },
        productionLines: { 
          status: productionLinesResponse.status, 
          ok: productionLinesResponse.ok,
          statusText: productionLinesResponse.statusText,
          headers: productionLinesResponse.headers
        }
      });

      // Handle products response
      if (!productsResponse.ok) {
        throw new Error(`Products API error: ${productsResponse.status}`);
      }

      const productsData = await productsResponse.json();
      console.log('📦 Raw Products response:', productsData);
      console.log('📦 Products data structure:', {
        success: productsData.success,
        hasData: !!productsData.data,
        dataLength: productsData.data?.length,
        firstItem: productsData.data?.[0]
      });
      
      if (productsData.success && productsData.data && Array.isArray(productsData.data)) {
        const formattedProducts = productsData.data.map(product => ({
          value: String(product.id || product.product_id),
          label: product.englishName || product.name || product.model_number, // Display English name
          model_number: product.model_number,
          englishName: product.englishName || product.name,
          italianName: product.italianName || product.title
        }));
        setProducts(formattedProducts);
        console.log('✅ Formatted products:', formattedProducts);
        console.log('✅ Set products state with', formattedProducts.length, 'items');
      } else {
        console.error('❌ Invalid products response format:', productsData);
        throw new Error(`Invalid products response: ${JSON.stringify(productsData)}`);
      }

      // Handle production lines response
      if (!productionLinesResponse.ok) {
        throw new Error(`Production lines API error: ${productionLinesResponse.status}`);
      }

      const productionLinesData = await productionLinesResponse.json();
      console.log('🏭 Raw Production lines response:', productionLinesData);
      console.log('🏭 Production lines data structure:', {
        success: productionLinesData.success,
        hasData: !!productionLinesData.data,
        dataLength: productionLinesData.data?.length,
        firstItem: productionLinesData.data?.[0]
      });
      
      if (productionLinesData.success && productionLinesData.data && Array.isArray(productionLinesData.data)) {
        const formattedLines = productionLinesData.data.map(line => ({
          value: String(line.id || line.production_line_id),
          label: line.englishName || line.name || line.line_number, // Display English name
          line_number: line.line_number,
          englishName: line.englishName || line.name,
          italianName: line.italianName || line.title
        }));
        setProductionLines(formattedLines);
        console.log('✅ Formatted production lines:', formattedLines);
        console.log('✅ Set production lines state with', formattedLines.length, 'items');
      } else {
        console.error('❌ Invalid production lines response format:', productionLinesData);
        throw new Error(`Invalid production lines response: ${JSON.stringify(productionLinesData)}`);
      }
      
    } catch (err) {
      console.error('❌ Error fetching unit details options:', err);
      setError(`Failed to load options: ${err.message}`);
      setProducts([]);
      setProductionLines([]);
    } finally {
      setIsLoading(false);
    }
  };

  const handleInputChange = (field, value) => {
    setFormData(prev => ({ ...prev, [field]: value }));
    
    // If unit model is selected, store both English name and ID
    if (field === 'unitModel') {
      const selectedProduct = products.find(p => p.value === value);
      if (selectedProduct) {
        setFormData(prev => ({ 
          ...prev, 
          unitModel: selectedProduct.label, // English name for display
          unitModelId: value, // ID for backend
          unitModelName: selectedProduct.englishName, // Store English name
          modelNumber: selectedProduct.model_number // Store model number for backend
        }));
      }
    }
    
    // Similarly for production line
    if (field === 'productionLine') {
      const selectedLine = productionLines.find(l => l.value === value);
      if (selectedLine) {
        setFormData(prev => ({ 
          ...prev, 
          productionLine: selectedLine.label, // English name for display
          productionLineId: value, // ID for backend
          productionLineName: selectedLine.englishName, // Store English name
          lineNumber: selectedLine.line_number // Store line number for backend
        }));
      }
    }
  };

  return (
    <section className={cn("min-h-screen flex items-center justify-center p-4 bg-gray-50", className)}>
      <div className="w-full opacity-0 animate-[slideIn_0.5s_ease-out_forwards] [animation-delay:0.1s]">
        <Card className="max-w-md mx-auto rounded-xl shadow-lg bg-white border-0" style={{ width: "500px", maxWidth: "500px" }}>
          <CardContent className="p-8">
            {/* Header with Back Button */}
            <div className="flex items-center justify-between mb-6">
              <Button 
                variant="ghost" 
                onClick={onBackToDashboard}
                className="flex items-center gap-2 text-gray-600 hover:text-[#0098cb] p-2"
              >
                <ArrowLeft className="h-4 w-4" />
                {t('unit.back')}
              </Button>
            </div>

            <h1 className="text-2xl md:text-3xl font-bold text-[#0098cb] text-center mb-8">{t('unit.title')}</h1>
            
            {/* Error Message */}
            {error && (
              <div className="mb-4 p-3 bg-red-50 border border-red-200 rounded-lg text-red-700 text-sm">
                <p>{error}</p>
                <button 
                  onClick={fetchUnitDetailsOptions}
                  className="mt-2 text-red-600 underline hover:text-red-800"
                >
                  Try again
                </button>
              </div>
            )}
            
            <div className="space-y-6">
              <div className="space-y-2">
                <Label htmlFor="serialNumber" className="font-semibold uppercase tracking-wide text-sm text-gray-600">{t('unit.serial_number')}</Label>
                <div className="relative">
                  <BarChart2 className="absolute left-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-gray-400" />
                  <Input 
                    id="serialNumber" 
                    type="text" 
                    value={formData.serialNumber} 
                    readOnly={formData.inputMethod === 'scan'}
                    onChange={formData.inputMethod === 'enter' ? (e) => handleInputChange("serialNumber", e.target.value) : undefined}
                    placeholder={formData.inputMethod === 'enter' ? t('unit.serial_number') : ""}
                    className={`pl-10 h-12 border-gray-200 focus:border-[#0098cb] focus:ring-[#0098cb] rounded-lg ${
                      formData.inputMethod === 'scan' ? "bg-gray-50" : "bg-white"
                    }`}
                  />
                </div>
                {formData.inputMethod === 'enter' && (
                  <p className="text-xs text-gray-500 mt-1">{t('unit.enter_serial')}</p>
                )}
                {formData.inputMethod === 'scan' && (
                  <p className="text-xs text-gray-500 mt-1">{t('unit.scan_serial')}</p>
                )}
              </div>

              <div className="space-y-2">
                <Label htmlFor="workOrder" className="font-semibold uppercase tracking-wide text-sm text-gray-600">{t('unit.work_order')}</Label>
                <div className="relative">
                  <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-gray-400" />
                  <Input 
                    id="workOrder" 
                    type="text" 
                    value={formData.workOrder} 
                    onChange={e => handleInputChange("workOrder", e.target.value)} 
                    placeholder={t('unit.enter_work_order')} 
                    className="pl-10 h-12 border-gray-200 focus:border-[#0098cb] focus:ring-[#0098cb] rounded-lg" 
                  />
                </div>
              </div>

              <div className="space-y-2">
                <Label htmlFor="unitModel" className="font-semibold uppercase tracking-wide text-sm text-gray-600">{t('unit.unit_model')}</Label>
                <div className="relative">
                  <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-gray-400" />
                  <Input 
                    id="unitModel" 
                    type="text" 
                    value={formData.unitModel || ''} 
                    onChange={e => handleInputChange("unitModel", e.target.value)} 
                    placeholder={t('unit.enter_unit_model')} 
                    className="pl-10 h-12 border-gray-200 focus:border-[#0098cb] focus:ring-[#0098cb] rounded-lg" 
                  />
                </div>
                {/* Keep products data fetching for future dropdown restoration */}
                {/* Products loaded: {products.length}, Loading: {isLoading ? 'Yes' : 'No'} */}
              </div>

              <div className="space-y-2">
                <Label htmlFor="productionLine" className="font-semibold uppercase tracking-wide text-sm text-gray-600">{t('unit.production_line').replace('{0}', productionLines.length)}</Label>
                <div className="relative">
                  {isLoading ? (
                    <div className="h-12 border border-gray-200 rounded-lg flex items-center justify-center">
                      <Loader2 className="h-4 w-4 animate-spin text-gray-400" />
                      <span className="ml-2 text-sm text-gray-500">{t('unit.loading_production_lines')}</span>
                    </div>
                  ) : (
                    <>
                      <Select value={formData.productionLineId || ''} onValueChange={value => handleInputChange("productionLine", value)}>
                        <SelectTrigger id="productionLine" className="h-12 border-gray-200 focus:border-[#0098cb] focus:ring-[#0098cb] rounded-lg pr-10 text-left">
                          {formData.productionLine ? (
                            <span className="text-gray-900">{formData.productionLine}</span>
                          ) : (
                            <span className="text-gray-500">{t('unit.select_production_line')}</span>
                          )}
                        </SelectTrigger>
                        <SelectContent className="bg-white border border-gray-200 shadow-lg rounded-lg max-h-60 overflow-y-auto">
                          {productionLines.length === 0 ? (
                            <div className="p-2 text-sm text-gray-500 text-center">{t('unit.no_production_lines')}</div>
                          ) : (
                            productionLines.map((line) => (
                              <SelectItem 
                                key={line.value} 
                                value={line.value} 
                                className="hover:bg-gray-50 cursor-pointer"
                              >
                                {line.label}
                              </SelectItem>
                            ))
                          )}
                        </SelectContent>
                      </Select>
                    </>
                  )}
                </div>
              </div>

              <div className="space-y-2">
                <Label htmlFor="dateTime" className="font-semibold uppercase tracking-wide text-sm text-gray-600">{t('unit.date_time')}</Label>
                <div className="relative">
                  <Calendar className="absolute left-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-gray-400" />
                  <Input 
                    id="dateTime" 
                    type="text" 
                    value={formData.dateTime} 
                    readOnly 
                    className="pl-10 h-12 bg-gray-50 border-gray-200 focus:border-[#0098cb] focus:ring-[#0098cb] rounded-lg" 
                  />
                </div>
              </div>

              <div className="space-y-2">
                <Label htmlFor="unitLock" className="font-semibold uppercase tracking-wide text-sm text-gray-600">{t('unit.unit_lock')}</Label>
                <div className="flex items-center justify-between p-3 border border-gray-200 rounded-lg">
                  <div className="flex items-center">
                    <Lock className="h-4 w-4 text-gray-400 mr-2" />
                    <span className="text-sm text-gray-700">
                      {t('unit.unit_is').replace('{0}', formData.unitLock ? t('unit.locked') : t('unit.unlocked'))}
                    </span>
                  </div>
                  <button
                    type="button"
                    onClick={() => handleInputChange("unitLock", !formData.unitLock)}
                    className={`relative inline-flex h-6 w-11 items-center rounded-full transition-colors focus:outline-none focus:ring-2 focus:ring-[#0098cb] focus:ring-offset-2 ${
                      formData.unitLock ? "bg-[#0098cb]" : "bg-gray-200"
                    }`}
                  >
                    <span
                      className={`inline-block h-4 w-4 transform rounded-full bg-white transition-transform ${
                        formData.unitLock ? "translate-x-6" : "translate-x-1"
                      }`}
                    />
                  </button>
                </div>
              </div>

              <div className="pt-4">
                <Button 
                  onClick={onSubmit}
                  disabled={isLoading || !formData.unitModel || !formData.productionLineId || (formData.inputMethod === 'enter' && !formData.serialNumber)}
                  className="w-full h-12 bg-[#2563eb] hover:bg-[#1d4ed8] text-white font-semibold rounded-lg transition-colors duration-200 focus:ring-2 focus:ring-[#2563eb] focus:ring-offset-2 disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  <span className="flex items-center justify-center gap-2">
                    {t('unit.continue')}
                    <ArrowRight className="h-4 w-4" />
                  </span>
                </Button>
              </div>
            </div>
          </CardContent>
        </Card>
      </div>

      <style jsx>{`
        @keyframes slideIn {
          from { opacity: 0; transform: translateY(20px); }
          to { opacity: 1; transform: translateY(0); }
        }
      `}</style>
    </section>
  );
}